import { useEffect, useRef, useState } from 'react';
import { useDebouncedValue, SEARCH_DEBOUNCE_MS } from './useDebouncedValue';
import { useCompositionSettledValue } from './useCompositionSettledValue';

/**
 * 與網址參數同步的搜尋框：輸入先存本地，停頓後才呼叫 onCommit 寫回網址；
 * 網址被其他操作改變（清除篩選、手機版篩選套用、上一頁）時同步回輸入框。
 *
 * 輸入框若直接綁網址參數，每打一個字就寫一次網址：每個字打一次 API、回應亂序時顯示舊關鍵字的結果，
 * 寫網址若沒用 replace 還會每個字留一筆瀏覽紀錄。
 *
 * @param {string} urlValue 網址上目前的值
 * @param {Function} onCommit (value) => void，把值寫回網址；請用 replace，避免每次停頓都新增一筆瀏覽紀錄
 * @returns {[string, Function, Object]} [輸入框的值, 更新輸入框的函式, 要展開到輸入框上的組字事件 props]
 *   輸入法組字期間不送出（見 useCompositionSettledValue）
 */
export function useDebouncedSearchInput(urlValue, onCommit) {
  const [input, setInput] = useState(urlValue);
  const { settledValue, compositionProps } = useCompositionSettledValue(input);
  const debouncedInput = useDebouncedValue(settledValue, SEARCH_DEBOUNCE_MS);

  // 以 ref 讀最新值：effect 只該在各自的觸發值改變時執行
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;
  const urlValueRef = useRef(urlValue);
  urlValueRef.current = urlValue;
  const debouncedInputRef = useRef(debouncedInput);
  debouncedInputRef.current = debouncedInput;

  // 停頓後寫回網址
  useEffect(() => {
    if (debouncedInput !== urlValueRef.current) {
      onCommitRef.current(debouncedInput);
    }
  }, [debouncedInput]);

  // 網址被其他操作改變時同步回輸入框；自己寫回網址造成的改變與 debouncedInput 相同，略過
  useEffect(() => {
    if (urlValue !== debouncedInputRef.current) {
      setInput(urlValue);
    }
  }, [urlValue]);

  return [input, setInput, compositionProps];
}
